import {
  Briefcase,
  HandCoins,
  ChartNoAxesCombined,
  Gift,
  Inbox,
  Receipt,
  Scale,
  Settings,
  Tag,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import * as React from 'react';
import {useLocation} from 'react-router-dom';
import {BrandMark} from 'src/BrandMark';
import {cn} from 'src/lib/utils';
import LogoutButton from 'src/LogoutButton';
import {SETTINGS_PAGES} from 'src/SettingsView';
import {ExpandableMenuSection} from 'src/SideNav/ExpandableMenuSection';
import {LightDarkThemeButton} from 'src/SideNav/LightDarkThemeButton';
import {MenuLink} from 'src/SideNav/MenuItem';
import {Separator} from 'src/SideNav/Separator';
import {SyncButton} from 'src/SideNav/SyncButton';

type Props = {
  isOpen: boolean;
};

const SideNav = (props: Props) => {
  const {isOpen} = props;
  const location = useLocation();

  // The hub's child pages lost their own sidebar slots, so the Settings
  // entry stays highlighted while the user is anywhere in that area.
  const inSettingsArea = ['/settings', ...SETTINGS_PAGES.map((p) => p.path)]
    .some(
      (path) =>
        location.pathname === path ||
        location.pathname.startsWith(`${path}/`),
    );

  const content = (
    <>
      <div className="text-sidebar-foreground flex-0 flex items-center gap-2.5 px-3 pt-2 pb-1">
        <BrandMark className="size-6 shrink-0" />
        <span className="font-semibold tracking-tight">Transactions</span>
      </div>

      <div className="flex-0">
        <MenuLink
          path="/"
          label="Inbox"
          iconLeft={<Inbox />}
        />
        <MenuLink
          path="/ledger"
          label="Ledger"
          iconLeft={<Receipt />}
        />
        <MenuLink
          path="/analytics"
          label="Analytics"
          iconLeft={<ChartNoAxesCombined />}
        />
        <MenuLink
          path="/accounts"
          label="Accounts"
          iconLeft={<Wallet />}
        />
        <MenuLink
          path="/investments"
          label="Investments"
          iconLeft={<TrendingUp />}
        />
        <ExpandableMenuSection
          label="More"
          childPaths={['/net-worth', '/payroll', '/equity', '/benefits']}
          storageKey="sideNavMoreOpen"
        >
          <MenuLink
            path="/net-worth"
            label="Net worth"
            iconLeft={<Scale />}
            level="secondary"
          />
          <MenuLink
            path="/payroll"
            label="Payroll"
            iconLeft={<HandCoins />}
            level="secondary"
          />
          <MenuLink
            path="/equity"
            label="Equity"
            iconLeft={<Briefcase />}
            level="secondary"
          />
          <MenuLink
            path="/benefits"
            label="Benefits"
            iconLeft={<Gift />}
            level="secondary"
          />
        </ExpandableMenuSection>
      </div>

      <Separator />

      <div className="flex-0">
        <MenuLink
          path="/categories"
          label="Categories"
          iconLeft={<Tag />}
        />
        <MenuLink
          path="/settings"
          label="Settings"
          isActive={inSettingsArea}
          iconLeft={<Settings />}
        />
      </div>

      <div className="flex-1" />

      <Separator />

      <div className="flex-0">
        <LightDarkThemeButton />
        <SyncButton />
        <LogoutButton />
      </div>
    </>
  );

  return (
    <div
      className={cn(
        'relative flex',
        'transition-all duration-300 ease-out',
        isOpen ? 'w-[300px] min-w-[300px]' : 'w-0 min-w-0',
      )}
    >
      <div
        role="navigation"
        aria-label="Primary navigation"
        className={cn(
          'h-full w-[300px] min-w-[300px]',
          'absolute top-0 right-0',
          'bg-sidebar border-border flex flex-col gap-2 overflow-y-auto border-r px-2 py-2',
        )}
      >
        {content}
      </div>
    </div>
  );
};

export default SideNav;
